
import React from 'react';
import GlassCard from '@/components/ui/GlassCard';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/components/i18n/language-provider';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface ProductFeature {
  name: string;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  features: ProductFeature[];
  color: string;
  icon: React.ReactNode;
  link?: string;
}

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { t } = useLanguage();
  
  const getTextColor = () => {
    switch (product.color) {
      case 'pink':
        return 'text-brand-pink';
      case 'purple':
        return 'text-brand-purple';
      case 'blue':
        return 'text-blue-500';
      case 'b688a4':
        return 'text-[#b688a4]';
      default:
        return 'text-primary';
    }
  };

  const getButtonColor = () => {
    switch (product.color) {
      case 'pink':
        return 'bg-brand-pink hover:bg-brand-pink/90 text-white';
      case 'purple':
        return 'bg-brand-purple hover:bg-brand-purple/90 text-white';
      case 'blue':
        return 'bg-blue-500 hover:bg-blue-500/90 text-white';
      case 'b688a4':
        return 'bg-[#b688a4] hover:bg-[#b688a4]/90 text-white';
      default:
        return '';
    }
  };

  const cardContent = (
    <>
      <div className="mb-4">
        <div className={cn("h-8 w-8", getTextColor())}>
          {product.icon}
        </div>
      </div>
      <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
      <p className="text-muted-foreground mb-6">{product.description}</p>
      
      <div className="mt-auto w-full mb-8">
        <h4 className="text-lg font-medium mb-3">{t('products.keyFeatures')}</h4>
        <ul className="space-y-2">
          {product.features.map((feature, i) => (
            <li key={i} className="flex items-start">
              <span className={cn("mr-2 text-lg", getTextColor())}>•</span>
              <span>{feature.name}</span>
            </li>
          ))}
        </ul>
      </div>
      
      {product.link && (
        <div className="mt-auto">
          <Button 
            className={cn("w-full gap-2 mt-2", getButtonColor())}
            asChild
          >
            <Link to={product.link}>
              {t('products.discoverMore')} <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}
    </>
  );

  return (
    <GlassCard className="flex flex-col items-start h-full" hoverEffect>
      {cardContent}
    </GlassCard>
  );
};

export default ProductCard;
